// Flow keys follow `<tenant_type>[_<role>]_onboarding`. The same string is
// used as the `flow_key` column value, the API segment (`/onboarding/<key>`),
// the config module name, the RBAC resource, and the satellite table prefix.
export const ONBOARDING_FLOW_KEYS = [
  'carrier_onboarding',
  'standard_driver_onboarding',
  'carrier_truck_onboarding',
  'headcount_onboarding',
] as const
export type OnboardingFlowKey = (typeof ONBOARDING_FLOW_KEYS)[number]

// The office pages read a flow key off the wire as a plain string. A guard
// rather than a cast, so a key the registry does not know narrows to nothing
// instead of being handed to a route that will 404 on it.
export function isOnboardingFlowKey(value: string | null | undefined): value is OnboardingFlowKey {
  return (ONBOARDING_FLOW_KEYS as readonly string[]).includes(value ?? '')
}

// The one flow that onboards a person rather than a company. Label sites, the
// driver-side document routers and the `standard_driver_onboarding_details`
// satellite all branch on it, so the product decision lives here rather than as
// inlined key comparisons.
//
// This used to be one of two predicates. `isCarrierDriverFlow` and
// `isDriverOnboardingFlow` both disappeared with `carrier_driver_onboarding`
// (PET-3167 phase 2): once a carrier hires a driver straight into Fleet there is
// only one driver-flavored flow left, and keeping three names for one string
// comparison invited call sites to disagree about which one they meant.
export const OWNER_OPERATOR_FLOW_KEY = 'standard_driver_onboarding'

export function isOwnerOperatorFlow(flowKey: string): flowKey is typeof OWNER_OPERATOR_FLOW_KEY {
  return flowKey === OWNER_OPERATOR_FLOW_KEY
}

// Upper bound on the vehicles an applicant can declare on one application.
// Owner-operators legitimately run more than one unit; the cap only exists so a
// malformed client can't post an unbounded array.
export const MAX_ONBOARDING_VEHICLES = 20

// The truck types every fresh tenant is seeded with (`provisionTenantDefaults`).
//
// Lives here rather than beside the provisioning code because the carrier
// onboarding wizard has to offer the same list: `sales.trucks.truck_type_id` is
// NOT NULL, and the applicant fills a pair card in before the carrier tenant —
// and therefore its `truck_types` rows — exists. The card stores the LABEL and
// the handoff resolves it to that tenant's row at approval, so the two lists
// must not drift.
export const DEFAULT_TRUCK_TYPES = [
  { label: 'Dump Truck', cdlClass: 'B', description: 'Standard dump truck' },
  { label: 'Tri-Axle Dump', cdlClass: 'B', description: 'Tri-axle dump truck' },
  { label: 'Tandem Dump', cdlClass: 'B', description: 'Tandem-axle dump truck' },
  { label: 'Semi / End Dump', cdlClass: 'A', description: 'Tractor with end-dump trailer' },
  { label: 'Flatbed', cdlClass: 'A', description: 'Flatbed trailer' },
] as const

export const DEFAULT_TRUCK_TYPE_LABELS: readonly string[] = DEFAULT_TRUCK_TYPES.map((t) => t.label)

// There is no Emblem-specific cap. PET-3151 E2 / A16 asked for the self-serve
// Emblem path to stop at five trucks and hand anything larger to Emblem's manual
// process; product reversed that (2026-08-27) — a carrier declares its whole
// fleet here whichever way it is insured. `MAX_ONBOARDING_VEHICLES` is the only
// ceiling, and it is a malformed-client guard rather than a business rule.

// Which entity inside an application a document requirement attaches to.
// `company` is the whole application, the only shape that existed before
// PET-3167. `truck` and `driver` attach per declared fleet unit, so a carrier
// declaring three trucks owes three registrations rather than one slot holding
// three files. The axis is orthogonal to `isRequired` and `conditionKey`: scope
// says WHERE the document hangs, not whether it is owed.
export const ONBOARDING_REQUIREMENT_SCOPES = ['company', 'truck', 'driver'] as const
export type OnboardingRequirementScope = (typeof ONBOARDING_REQUIREMENT_SCOPES)[number]

// The subject a document row hangs off, mirroring `applications.subject_kind`.
// A NULL subject on a document means company-level, whatever its requirement's
// scope says — that is what keeps documents uploaded before a slug moved to
// per-unit scope readable instead of orphaned.
export const ONBOARDING_DOCUMENT_SUBJECT_KINDS = [
  'application_vehicle',
  'application_driver',
] as const
export type OnboardingDocumentSubjectKind = (typeof ONBOARDING_DOCUMENT_SUBJECT_KINDS)[number]

export function isOnboardingDocumentSubjectKind(
  value: string,
): value is OnboardingDocumentSubjectKind {
  return (ONBOARDING_DOCUMENT_SUBJECT_KINDS as readonly string[]).includes(value)
}

export const ONBOARDING_PHASES = [
  'invited',
  'in_progress',
  'submitted',
  'in_review',
  'approved',
  'rejected',
  'closed',
] as const
export type OnboardingPhase = (typeof ONBOARDING_PHASES)[number]

// Terminal phases — application is done. Mirrors the WHERE clause of the
// `applications_active_email_uq` / `applications_active_subject_uq` partial
// unique indexes; the runtime "is this app still active?" check must stay in
// lockstep with those indexes.
export const ONBOARDING_TERMINAL_PHASES = [
  'rejected',
  'closed',
] as const satisfies readonly OnboardingPhase[]

// Takes a bare string so the applicant-facing clients can call it on a phase
// straight off the wire without first narrowing it to `OnboardingPhase`.
export function isTerminalOnboardingPhase(phase: string | null | undefined): boolean {
  return (ONBOARDING_TERMINAL_PHASES as readonly string[]).includes(phase ?? '')
}

// Wider than ONBOARDING_TERMINAL_PHASES by exactly `approved`, and deliberately
// a separate list rather than a widening of that one: the terminal list is held
// in lockstep with the `applications_active_*_uq` partial unique indexes, where
// an approved application still counts as active, so adding a phase there would
// change which applications may coexist.
//
// "Decided" is the office's question, not the applicant's. ONB-26 locks the
// applicant out of a rejected record because the office reviewed the values on
// file; this locks the OFFICE out of an approved one, because approval mints a
// tenant and its company row from those very columns (CARR-05) and promotes the
// declared fleet with them (CARR-19) — so an edit afterwards leaves the
// application and the real carrier disagreeing with nothing to reconcile them.
export const ONBOARDING_DECIDED_PHASES = [
  ...ONBOARDING_TERMINAL_PHASES,
  'approved',
] as const satisfies readonly OnboardingPhase[]

// Takes a bare string for the same reason `isTerminalOnboardingPhase` does: the
// office pages call it on a phase straight off the wire. Shared with the server
// so a hidden button and a refused request cannot disagree (`DOC-10`).
export function isDecidedOnboardingPhase(phase: string | null | undefined): boolean {
  return (ONBOARDING_DECIDED_PHASES as readonly string[]).includes(phase ?? '')
}

// When the office may no longer correct an application's record (CARR-37).
// A carrier's record freezes at approval because approval mints a tenant from
// it with nothing to reconcile the two afterwards. An owner operator's does
// not: every value approval copied has a write-through, so the office keeps
// correcting a live operator from the onboarding page. Rejected and closed stay
// frozen for both, because a resubmission clone carries the record forward.
export function isOfficeRecordFrozen(
  flowKey: string | null | undefined,
  phase: string | null | undefined,
): boolean {
  if (flowKey && isOwnerOperatorFlow(flowKey)) return isTerminalOnboardingPhase(phase)
  return isDecidedOnboardingPhase(phase)
}

export const ONBOARDING_EVENT_ACTOR_TYPES = ['office', 'applicant', 'system', 'external'] as const
export type OnboardingEventActorType = (typeof ONBOARDING_EVENT_ACTOR_TYPES)[number]

export const ONBOARDING_DOCUMENT_SOURCES = [
  'applicant_upload',
  'system_generated',
  'signing_flow',
] as const
export type OnboardingDocumentSource = (typeof ONBOARDING_DOCUMENT_SOURCES)[number]

// `driver_upload` predates `applicant_upload` and is not in the union above, so it
// only compiles where the column is typed as a bare string. Rows still carrying it
// are applicant uploads, and every surface that offers an upload slot has to treat
// them as such — hence one predicate rather than the comparison inlined at each
// call site, which is how it reached eleven copies.
//
// Delete this once no `document_requirements` row has `source = 'driver_upload'`.
const LEGACY_APPLICANT_UPLOAD_SOURCE = 'driver_upload'

export function isApplicantUploadSource(source: string): boolean {
  return source === 'applicant_upload' || source === LEGACY_APPLICANT_UPLOAD_SOURCE
}

// The same set as a value, for SQL `IN (...)`. Filtering
// `ONBOARDING_DOCUMENT_SOURCES` by the predicate above does NOT produce this:
// `driver_upload` is deliberately absent from that union, so the filter silently
// drops the one value the predicate exists to cover, and the result reads as
// correct while under-counting every legacy row.
export const APPLICANT_UPLOAD_SOURCES: readonly string[] = [
  'applicant_upload',
  LEGACY_APPLICANT_UPLOAD_SOURCE,
]

// Gating predicates for `document_requirements.condition_key`. A NULL column
// means "applies on every path" — these values narrow a requirement to one
// insurance path or to drivers past the DOT medical-card age threshold. They
// carry no required/optional meaning: that is `is_required` alone. (The legacy
// `'both'` sentinel these replaced conflated the two axes, which is what let
// the whole Workers' Comp group read as mandatory.)
export const ONBOARDING_DOCUMENT_CONDITION_KEYS = [
  'has_own',
  'needs_insurance',
  'age_65_plus',
  // The carrier's owner ticked "I will drive myself", so documents about the
  // person behind the wheel apply to the company application too.
  'owner_drives',
  // Same threshold as `age_65_plus`, inverted on an unknown date of birth. The
  // asymmetry is the point and is explained where the two are defined together,
  // in documentConditions.ts.
  'owner_age_65_plus',
  // The trucker accepted the Emblem quote, so coverage is bound and the
  // certificate now exists. Unlike every other atom here this one tracks a
  // STATE the application moves through rather than an answer on the form, so
  // it can flip long after the applicant last touched anything — which is the
  // whole point: the COI cannot be asked for before there is one.
  'emblem_quote_accepted',
] as const
export type OnboardingDocumentConditionKey = (typeof ONBOARDING_DOCUMENT_CONDITION_KEYS)[number]

// Atoms combine with `&` into a conjunction, because a requirement can depend on
// more than one axis and `condition_key` is a single column: an owner-operator
// owes a medical card only when Emblem underwrites them AND they are 65+.
//
// Conjunctions combine further with `|` into a disjunction, evaluated as an
// OR-of-ANDs (`&` binds tighter). One requirement can be owed for genuinely
// unrelated reasons: the COI is owed by a trucker who already carries a policy,
// OR by one Emblem just bound coverage for. Without `|` that needs two catalog
// rows for one slug, which the `(flow, slug)` open-row unique forbids.
//
// Spelling the combinations out keeps the type checked rather than falling back
// to `string`. Conjunctions are enumerated as a cross product; DISJUNCTIONS ARE
// NOT. Crossing them too is what makes this type expensive — the union grows
// quartically in the atom count, and at six atoms a two-atom-disjunct cross
// product is already ~1,800 members whose only purpose is to describe keys the
// catalog does not contain. Every disjunctive key in use is listed explicitly
// below instead; add to that list when a new one appears.
type OnboardingDocumentConjunction =
  | OnboardingDocumentConditionKey
  | `${OnboardingDocumentConditionKey}&${OnboardingDocumentConditionKey}`
  | `${OnboardingDocumentConditionKey}&${OnboardingDocumentConditionKey}&${OnboardingDocumentConditionKey}`

// The disjunctive keys the catalog actually uses. One entry today: a COI is owed
// by a trucker who already carries a policy, OR by one Emblem just bound
// coverage for (PET-3166).
type OnboardingDocumentDisjunction = 'has_own|needs_insurance&emblem_quote_accepted'

export type OnboardingDocumentCondition =
  OnboardingDocumentConjunction | OnboardingDocumentDisjunction

// Split a stored `condition_key` into its clauses: `|`-separated alternatives,
// each an `&`-separated conjunction of atoms. The requirement applies when ANY
// clause has ALL of its atoms holding.
export function parseDocumentCondition(conditionKey: string): string[][] {
  return conditionKey.split('|').map((clause) => clause.split('&'))
}

// DOT requires a current medical examiner's certificate from drivers at or past
// this age.
export const MEDICAL_CARD_AGE_THRESHOLD = 65

// Onboarding forms started asking for a date of birth on this date. Applications
// created before it are never blocked for a missing one: they were filled in
// under a form that had no such field, and an in-flight application is evaluated
// against TODAY's catalog (`effectiveRequirementsFor` anchors on now, not on
// createdAt), so without this cutoff every open application would suddenly owe
// an answer it was never asked for. Compare against `applications.created_at`.
export const ONBOARDING_DOB_REQUIRED_FROM = new Date('2026-08-18T00:00:00.000Z')

// Whether an application must answer the date-of-birth question to move on.
export function requiresDateOfBirth(applicationCreatedAt: Date | string): boolean {
  const createdAt =
    applicationCreatedAt instanceof Date ? applicationCreatedAt : new Date(applicationCreatedAt)
  if (Number.isNaN(createdAt.getTime())) return false
  return createdAt.getTime() >= ONBOARDING_DOB_REQUIRED_FROM.getTime()
}

// Whether a declared fleet driver still owes their date of birth. Lives here
// because the applicant's Next button and the server's step / submit gates must
// agree exactly: the insurance path is an enum term, so a divergence would
// either let a driver through without the answer the `age_65_plus` atom depends
// on, or strand the applicant on a disabled button.
//
// Replaces the carrier flow's `isOwnerDateOfBirthOwed` (PET-3167): the owner is
// no longer the only person we underwrite on a carrier application, so the
// question is asked per driver on their pair card rather than once on the
// satellite. `requiresDateOfBirth` still shields applications filled in under a
// form that never had the field.
export function isFleetDriverDateOfBirthOwed(args: {
  insurancePath: string | null
  dateOfBirth: string | null
  applicationCreatedAt: Date | string
}): boolean {
  return (
    args.insurancePath === 'needs_insurance' &&
    !args.dateOfBirth &&
    requiresDateOfBirth(args.applicationCreatedAt)
  )
}

export const ONBOARDING_DOCUMENT_STATUSES = [
  'draft',
  'pending',
  'approved',
  'rejected',
  'expired',
] as const
export type OnboardingDocumentStatus = (typeof ONBOARDING_DOCUMENT_STATUSES)[number]

// Cross-flow signature workflow state. Flows without a signature step don't
// create rows in `onboarding.application_signatures`; presence of a row means
// the flow reached a signing phase.
export const ONBOARDING_SIGNATURE_STATUSES = [
  'not_started',
  'awaiting_signature',
  // Multi-party signing: one party has signed, another still needs to.
  // E.g. driver signed, office countersignature pending.
  'partially_signed',
  'completed',
  'declined',
  'expired',
] as const
export type OnboardingSignatureStatus = (typeof ONBOARDING_SIGNATURE_STATUSES)[number]

// How long the office's owner-operator invite stays good, for the invite and the
// re-invite alike: the office does not pick it. The link signs the invitee in
// until then (`AUTH-40`), so it is kept short.
export const OWNER_OPERATOR_INVITE_EXPIRES_IN_DAYS = 7
